import { useEffect, useRef, useState, type CSSProperties } from "react";
import { formatDecimalPlaces } from "../../../../helpers/format-decimal-places";
import {
  SalesHistoryFilters,
  useGetAgenciesSalesDetails,
} from "../../../../hooks/agencies-sales-details/use-get-agencies-sales-details";
import { SaleHistoryItem } from "../../../../services/agencies-sales-details/types";
import { useUserTimezone } from "../../../../hooks/date/use-user-timezone";
import { useReverseAgencySaleDetail } from "../../../../hooks/agencies-sales-details/use-reverse-agency-sale-detail";
import { reverseErrorMessage } from "../../../../helpers/sale-error-message";

const AVATAR_FALLBACK = "https://didderlive.com/assets/didder-logo.png";

const HistoryRow = ({
  item,
  formatDate,
  onReverse,
}: {
  item: SaleHistoryItem;
  formatDate: (value?: string | null) => string;
  onReverse: (item: SaleHistoryItem) => void;
}) => {
  const title = item?.userUsername || `Usuário #${item?.userId}`;
  const isReversed = !!item?.reversedAt;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: isReversed ? "#faf7f7" : "#fff",
        borderRadius: "10px",
        padding: "14px 16px",
        marginBottom: "10px",
        gap: "12px",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        // A estornada continua na lista, mas fica visivelmente apagada: ela não
        // conta mais no total vendido.
        opacity: isReversed ? 0.72 : 1,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flex: 1,
          minWidth: 0,
        }}
      >
        <img
          src={item?.userImgUser || AVATAR_FALLBACK}
          onError={(e) => {
            e.currentTarget.src = AVATAR_FALLBACK;
          }}
          style={{
            width: "42px",
            height: "42px",
            borderRadius: "21px",
            objectFit: "cover",
            flexShrink: 0,
            background: "#eee",
          }}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              color: "#222",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </div>

          <div style={{ fontSize: "12px", color: "#777", marginTop: "2px" }}>
            ID {item?.userId}
          </div>

          <div style={{ fontSize: "12px", color: "#777", marginTop: "2px" }}>
            {formatDate(item?.dateSentCoin ?? item?.createdAt)}
          </div>

          {isReversed && (
            <div
              style={{
                display: "inline-block",
                marginTop: "6px",
                padding: "2px 8px",
                borderRadius: "10px",
                background: "#fdecea",
                color: "#b3261e",
                fontSize: "11px",
                fontWeight: "bold",
              }}
            >
              Estornada em {formatDate(item?.reversedAt)}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontSize: "16px",
            fontWeight: "bold",
            color: isReversed ? "#999" : "#2e7d32",
            whiteSpace: "nowrap",
            textDecoration: isReversed ? "line-through" : "none",
          }}
        >
          +{formatDecimalPlaces(item?.coins)}
        </div>

        {/* Estornar. Some quando a venda já foi estornada — estorno é uma vez só. */}
        {!isReversed && (
          <button
            type="button"
            onClick={() => onReverse(item)}
            title="Estornar esta venda"
            aria-label={`Estornar a venda de ${formatDecimalPlaces(
              item?.coins
            )} moedas para ${title}`}
            style={{
              // 44px é o alvo de toque mínimo confortável no celular — com 30px
              // o dedo erra a linha e acaba clicando no card.
              width: "44px",
              height: "44px",
              borderRadius: "22px",
              border: "1.5px solid #e79a94",
              // fundo preenchido pra ler como BOTÃO, não como um traço de texto
              background: "#fdecea",
              color: "#b3261e",
              fontSize: "26px",
              lineHeight: "1",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              flexShrink: 0,
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
            }}
          >
            −
          </button>
        )}
      </div>
    </div>
  );
};

const labelStyle: CSSProperties = {
  fontSize: "12px",
  fontWeight: "bold",
  color: "#555",
  marginBottom: "4px",
  display: "block",
};

const inputStyle: CSSProperties = {
  width: "100%",
  padding: "10px",
  background: "#f0f3fc",
  border: "1px solid #ccc",
  borderRadius: "6px",
  fontSize: "14px",
  boxSizing: "border-box",
};

export const SalesHistory = () => {
  const { formatInZone, zonedBoundToUtc, todayInZone } = useUserTimezone();

  const [userIdInput, setUserIdInput] = useState("");
  const [dateInitial, setDateInitial] = useState("");
  const [dateFinal, setDateFinal] = useState("");
  const [filters, setFilters] = useState<SalesHistoryFilters>({});

  // Venda escolhida no "−", aguardando confirmação no modal.
  const [toReverse, setToReverse] = useState<SaleHistoryItem | null>(null);
  const [feedback, setFeedback] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const { reverse, isLoading: isReversing } = useReverseAgencySaleDetail();

  const {
    sales,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAgenciesSalesDetails(filters);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // scroll infinito: quando a sentinela (fim da lista) entra na viewport,
  // busca a próxima página. rootMargin adianta o carregamento.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSearch = () => {
    const trimmed = userIdInput.trim();
    const parsedId = trimmed ? Number(trimmed) : undefined;

    setFilters({
      userId:
        parsedId !== undefined && Number.isFinite(parsedId)
          ? parsedId
          : undefined,
      // datas escolhidas no fuso do usuário -> limites UTC (comparáveis ao created_at)
      dateInitial: zonedBoundToUtc(dateInitial, "00:00:00", false),
      dateFinal: zonedBoundToUtc(dateFinal, "23:59:59", true),
    });
  };

  // Confirma o estorno. A API é quem decide: se o cliente não tiver mais o valor
  // completo na carteira, ela recusa e NADA é alterado — a mensagem devolvida diz
  // isso com todas as letras.
  const handleConfirmReverse = async () => {
    if (!toReverse || isReversing) {
      return;
    }

    try {
      const response = await reverse({ saleId: toReverse.id });

      if (response?.status === "success" && response?.data?.reversed) {
        setFeedback({
          ok: true,
          text: `Venda de ${formatDecimalPlaces(
            toReverse.coins
          )} moedas estornada. As moedas voltaram para o seu estoque.`,
        });
      } else {
        setFeedback({ ok: false, text: reverseErrorMessage(response?.error) });
      }
    } catch {
      setFeedback({ ok: false, text: reverseErrorMessage() });
    }

    setToReverse(null);
  };

  const handleClear = () => {
    setUserIdInput("");
    setDateInitial("");
    setDateFinal("");
    setFilters({});
  };

  return (
    <div>
      {/* filtros: User ID + intervalo de data */}
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          padding: "14px 16px",
          marginBottom: "14px",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div style={{ marginBottom: "10px" }}>
          <label style={labelStyle}>User ID</label>
          <input
            type="number"
            inputMode="numeric"
            placeholder="ID do usuário (opcional)"
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={labelStyle}>De</label>
            <input
              type="date"
              value={dateInitial}
              max={dateFinal || todayInZone()}
              onChange={(e) => setDateInitial(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={labelStyle}>Até</label>
            <input
              type="date"
              value={dateFinal}
              min={dateInitial || undefined}
              max={todayInZone()}
              onChange={(e) => setDateFinal(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={handleSearch}
            style={{
              flex: 1,
              background: "#3f51b5",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "10px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            Pesquisar
          </button>

          <button
            onClick={handleClear}
            style={{
              background: "transparent",
              color: "#3f51b5",
              border: "1px solid #3f51b5",
              borderRadius: "6px",
              padding: "10px 16px",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            Limpar
          </button>
        </div>
      </div>

      {/* resultado do último estorno */}
      {feedback && (
        <div
          style={{
            background: feedback.ok ? "#e8f5e9" : "#fdecea",
            color: feedback.ok ? "#1b5e20" : "#b3261e",
            border: `1px solid ${feedback.ok ? "#c8e6c9" : "#f5c6c3"}`,
            borderRadius: "10px",
            padding: "12px 14px",
            marginBottom: "12px",
            fontSize: "13px",
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <span style={{ flex: 1 }}>{feedback.text}</span>
          <button
            type="button"
            onClick={() => setFeedback(null)}
            aria-label="Fechar aviso"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              fontSize: "16px",
              cursor: "pointer",
              lineHeight: 1,
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* lista */}
      {isLoading ? (
        <div style={{ color: "#777", padding: "10px 0" }}>Carregando...</div>
      ) : !sales.length ? (
        <div style={{ color: "#777", padding: "10px 0" }}>
          Nenhuma venda encontrada
        </div>
      ) : (
        sales.map((item) => (
          <HistoryRow
            key={item.id}
            item={item}
            formatDate={formatInZone}
            onReverse={(sale) => {
              setFeedback(null);
              setToReverse(sale);
            }}
          />
        ))
      )}

      {/* sentinela do scroll infinito */}
      <div ref={sentinelRef} style={{ height: "1px" }} />

      {isFetchingNextPage && (
        <div
          style={{ color: "#777", padding: "10px 0", textAlign: "center" }}
        >
          Carregando mais...
        </div>
      )}

      {!isLoading && !hasNextPage && sales.length > 0 && (
        <div
          style={{
            color: "#aaa",
            padding: "10px 0",
            textAlign: "center",
            fontSize: "12px",
          }}
        >
          Fim do histórico
        </div>
      )}

      {/* modal de confirmação do estorno */}
      {toReverse && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Confirmar estorno da venda"
          onClick={() => !isReversing && setToReverse(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "20px",
              width: "100%",
              maxWidth: "360px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "bold",
                color: "#222",
                marginBottom: "10px",
              }}
            >
              Estornar esta venda?
            </div>

            <div
              style={{ fontSize: "14px", color: "#555", lineHeight: "1.5" }}
            >
              <b>{formatDecimalPlaces(toReverse.coins)} moedas</b> serão
              retiradas de{" "}
              <b>{toReverse.userUsername || `Usuário #${toReverse.userId}`}</b>{" "}
              (ID {toReverse.userId}) e devolvidas ao seu estoque.
            </div>

            <div
              style={{
                fontSize: "12px",
                color: "#777",
                marginTop: "10px",
                lineHeight: "1.5",
              }}
            >
              Se o cliente já tiver gasto parte das moedas, o estorno não é
              feito — é tudo ou nada. A venda continua no histórico, marcada
              como estornada, e deixa de contar no total vendido.
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "18px",
              }}
            >
              <button
                type="button"
                onClick={() => setToReverse(null)}
                disabled={isReversing}
                style={{
                  flex: 1,
                  background: "transparent",
                  color: "#3f51b5",
                  border: "1px solid #3f51b5",
                  borderRadius: "6px",
                  padding: "10px",
                  fontSize: "14px",
                  cursor: isReversing ? "default" : "pointer",
                  opacity: isReversing ? 0.6 : 1,
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={handleConfirmReverse}
                disabled={isReversing}
                style={{
                  flex: 1,
                  background: "#b3261e",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "10px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  cursor: isReversing ? "default" : "pointer",
                  opacity: isReversing ? 0.7 : 1,
                }}
              >
                {isReversing ? "Estornando..." : "Estornar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
